
// Class for defining declarative and composable model modifcations:
export default class BCMSModeler {

  // CONSTRUCTOR :: OBJECT -> this
  // NOTE: Stores model as cloned object, sets empty object by default:
  constructor(model) {
    this.model = model === undefined ? {} : BCMSModeler.clone(model);
  }

  // ap :: (model -> model) -> this
  // "Applies" function to model:
  ap(fn) {
    this.model = fn(this.model);
    return this;
  }

  // apif :: (model -> BOOLEAN), (model -> model) -> this
  // Applies function to model if predicate applied to model is true:
  apif (pred, fn) {
    this.model = pred(this.model) ? fn(this.model) : this.model;
    return this;
  }

  // apifnot :: (model -> model) -> this
  // Funciton is only applied to an "empty" model
  // NOTE: An "empty" model is a model that has no keys:
  apifnot (fn) {
    this.model = Object.keys(this.model).length < 0 ? fn(this.model) : this.model;
    return this;
  }

  // apto :: STRING, (*, model) -> *) -> this
  // Applies function to property of model:
  apto (id, fn) {
    this.model[id] = fn(this.model[id], this.model);
    return this;
  }

  // aptoif :: (model -> BOOLEAN), STRING, (*, model -> *) -> this
  // Applies function to property of model if predicate applied to model is true:
  aptoif (pred, id, fn) {
    this.model = pred(this.model) ? fn(this.model[id], this.model) : this.model;
    return this;
  }

  // aptoifnot :: STRING, (*, OBJECT -> *) -> this
  // Only applies function to property of model if property is undefined:
  aptoifnot (id, fn) {
    this.model[id] = this.model[id] === undefined ? fn(this.model) : this.model[id];
    return this;
  }

  // mapto :: STRING, (* -> * -> [*]) -> this
  // Applies map to property:
  // NOTE: Value stored in property MUST be an array:
  mapto (id, fn) {
    if (Array.isArray(this.model[id])) {
      return this.apto(id, function (prop, model) {
        return prop.map(fn);
      })
    } else {
      throw new Error("Property \"" + id + "\" of model must be an Array!");
    }
  }

  // filterto :: STRING, (* -> BOOLEAN -> [*]) -> this
  // Applies filter to property:
  // NOTE: Value stored in property MUST be an array:
  filterto (id, fn) {
    if (Array.isArray(this.model[id])) {
      return this.apto(id, function (prop, model) {
        return prop.filter(fn);
      });
    } else {
      throw new Error("Property \"" + id + "\" of model must be an Array!");
    }
  }

  // reduceto :: STRING, (a, b -> a), a -> this
  // Applies reduce to property:
  // NOTE: Value stored in property MUST be an array:
  reduceto (id, fn, acc) {
    if (Array.isArray(this.model[id])) {
      return this.apto(id, function (prop, model) {
        return prop.reduce(fn, acc);
      });
    } else {
      throw new Error("Property \"" + id + "\" of model must be an Array!");
    }
  }

  // apfrom :: STRING, STRING, (*, *, MODEL -> *) -> this
  // Applies function to a "from" property and write it "to" another property:
  // NOTE: Value stored in property MUST be an array:
  apfrom (from, to, fn) {
    this.model[to] = fn(this.model[from], this.model[to], this.model);
    return this;
  }

  // :: apfromit :: (*, * -> BOOLEAN), STRING, STRING, (*, * -> *) -> this
  // Function is applied "from" given property "to" given property if predicat returns true
  // when applied to the "from" property and "to" property:
  apfromif (pred, from, to, fn) {
    return pred(model[from], model[to]) ? this.apfrom(from, to, fn) : this;
  }

  // mapfrom :: STRING, STRING, (* -> * -> [*]) -> this
  // Applies map to a "from" property and stores result "to" another property:
  // NOTE: Value stored in "from" MUST be an array:
  mapfrom (from, to, fn) {
    if (Array.isArray(this.model[from])) {
      return this.apfrom(from, to, function (fromProp) {
        return fromProp.map(fn);
      });
    } else {
      throw new Error("Property \"" + from + "\" of model must be an Array!");
    }
  }

  // filterfrom :: STRING, STRING, (* -> BOOLEAN -> [*]) -> this
  // Applies filter to a "from" property and stores result "to" another property:
  // NOTE: Value stored in "from" MUST be an array:
  filterfrom (from, to, fn) {
    if (Array.isArray(this.model[from])) {
      return this.apfrom(from, to, function (fromProp) {
        return fromProp.filter(fn);
      });
    } else {
      throw new Error("Property \"" + from + "\" of model must be an Array!");
    }
  }

  // reducefrom :: STRING, STRING, (a, b -> a), a -> this
  // Applies reduce to a "from" property and stores result "to" another using the given acclumator:
  // NOTE: Value stored in "from" MUST be an array:
  reducefrom (from, to, fn, acc) {
    if (Array.isArray(this.model[from])) {
      return this.apfrom(from, to, function (fromProp) {
        return fromProp.reduce(fn, acc);
      });
    } else {
      throw new Error("Property \"" + from + "\" of model must be an Array!");
    }
  }

  // as :: STRING, * -> this
  // Bind value to property of model:
  as (id, val) {
    this.model[id] = val
    return this;
  }

  // asif :: (model -> BOOLEAN), STRING, * -> this
  // Binds value to property of model if predicate applied to model is true:
  asif (pred, id, val) {
    this.model[id] = pred(this.model) ? val : this.model[id];
    return this;
  }

  // asifnot :: (model -> BOOLEAN), STRING, * -> this
  // Binds value to property if property if undefined
  asifnot (id, val) {
    this.model[id] = this.model[id] === undefined ? val : this.model[id];
    return this;
  }

  // sortObjsBy :: STRING, (*, * -> BOOLEAN) -> this
  // Sorts array of values stored in property of model for given ID:
  // NOTE: Value stored in property by ID must be an array!
  sortBy(id, fn) {
    var arr = this.model[id];
    if (Array.isArray(arr)) {
      this.model[id] = arr.sort(fn)
    } else {
      throw new Error("Property \"" + id + "\" of model must be an Array!");
    }
    return this;
  }

  // sortObjsBy :: STRING, STRING, (*, * -> BOOLEAN) -> this
  // Sorts the objects stored in th given property by ID by the given function applied to property of objects stored in that ID:
  // NOTE: Value stored in property by ID must be an array!
  sortObjsBy (id, prop, fn) {
    return this.sortBy(id, function (a,b) {
      return fn(a[prop], b[prop]);
    })
  }

  // sortObjsByAsc :: STRING, STRING -> this
  // Sorts object stored in model by given property in ascending order (from "first" to "last")
  // NOTE: Value stored in property by ID must be an array!
  sortObjsByAsc (id, prop) {
    return this.sortObjsBy(id, prop, function (a,b) {
      a = isNaN(a) ? a.toLowerCase() : parseFloat(a);
      b = isNaN(b) ? b.toLowerCase() : parseFloat(b);
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    })
  }

  // sortObjsByDesc :: STRING, STRING -> this
  // Sorts object stored in model by given property in descending order (from "last" to "first")
  // NOTE: Value stored in property by ID must be an array!
  sortObjsByDesc (id, prop) {
    return this.sortObjsBy(id, prop, function (a,b) {
      a = isNaN(a) ? a.toLowerCase() : parseFloat(a) ;
      b = isNaN(b) ? b.toLowerCase() : parseFloat(b) ;
      if (a < b) return 1;
      if (a > b) return -1;
      return 0;
    })
  }

  // extractTo :: STRING, STRING, OBJECT, BOOLEAN -> this
  // Binds property of object to property of model:
  // NOTE: If clone is TRUE, and value of property is an object, that value is cloned
  // NOTE: Value of proprty of object is cloned by default!
  extractTo (id, prop, obj, clone) {
    var toClone = clone === undefined ? true : clone;
    var val = obj[prop];
    if (typeof(val) === "object") {
      this.model[id] = toClone == true ? BCMSModler.clone(val) : val;
    } else {
      this.model[id] = val;
    }
    return this;
  }

  // extract :: STRING, OBJECT -> this
  // Writes the prop of the given object to the prop of the model:
  // NOTE: If clone is TRUE, and value of property is an object, that value is cloned
  // NOTE: Value of proprty of object is cloned by default!
  extract (prop, obj, clone) {
    return this.extractTo(prop, prop, obj, clone);
  }

  // extractMany :: [STRING], OBJECT, BOOLEAN -> this
  // Extracts list of properties from object:
  // NOTE: If clone is TRUE, and value of property is an object, that value is cloned
  // NOTE: Value of proprty of object is cloned by default!
  extractMany (props, obj, clone) {
    return props.reduce(function (modeler, prop) {
      return modeler.extract(prop, obj, clone);
    }, this);
  }

  // :: STRING, STRING -> this
  // Clones value "from" a property in model and stores it "to" another property:
  copy (from, to) {
    var val = this.model[from];
    this.model[to] = typeof(val) === "object" ? BCMSModeler.clone(val) : val;
    return this;
  }

  // merge :: OBJECT, BOOLEAN -> this
  // Binds all properties of given object to model
  // NOTE: If overwrite is TRUE, properties in given object will overwrite model
  // NOTE: Overwrite is TRUE by default!
  // NOTE: If clone is TRUE, properties bound to model are from a clone of the given object
  // NOTE: Object being merged from is cloned by default!
  merge (obj, overwrite, clone) {
    var _clone = clone === undefined ? true : false
    var _overwrite = overwrite === undefined ? true : overwrite;
    var _obj = _clone === true ? BCMSModeler.clone(obj) : obj
    this.model = Object.keys(_obj).reduce(function (model, id) {
      if (model[id] === undefined || (model[id] !== undefined && _overwrite == true)) {
        model[id] = _obj[id];
      }
      return model;
    }, this.model);
    return this;
  }

  // mergeToEachWhere :: (*, * -> BOOLEAN), STRING, [*], STRING, STRING, BOOLEAN -> this
  // Applies a predicate to each element of the array bound to ID and the given array,
  // if that predicate is true, the value of propFrom the element of the given array
  // is bound to the property of the element of the array bound to model:
  // NOTE: If propFrom is undefined, then elemen is bount to "propTo"
  // NOTE: If propTo is undefined, value from propFrom is written to index
  // NOTE: Both proprty of model and given array must be array!
  mergeToEachWhere (pred, id, arr, propTo, propFrom, clone) {
    var _clone = clone === undefined ? true : clone;
    var _thisArr = this.model[id];
    if (Array.isArray(_thisArr) && Array.isArray(arr)) {
      var _arr = _clone === true ? BCMSModeler.clone(_arr) : _arr;
      return this.reduceto(id, function (result, elem, index) {
        if (pred(elem, arr[index]) === true) {
          var val = _arr[index][propFrom] === undefined ? _arr[index] :_arr[index][propFrom]
          if (elem[propTo] === undefined) {
            result.push(val)
          } else {
            elem[propTo] = val;
            result.push(elem)
          }
        }
        return result;
      }, [])
    } else {
      throw new Error("Can only merge each elements of an array to elements of another array");
    }
  }

  // mergeToEach :: STRING, [*], STRING, STRING, BOOLEAN -> this
  // Bind propFrom value to propTo property for every elem of array stored in ID from given array:
  // NOTE: If propFrom is undefined, then elemen is bount to "propTo"
  // NOTE: If propTo is undefined, value from propFrom is written to index
  // NOTE: Both proprty of model and given array must be array!
  mergeToEach (id, arr, propTo, propFrom, clone) {
    return this.mergeToEachWhere(function (a,b) {
      return true;
    }, id, arr, propTo, propFrom, clone);
  }

  // mergeFromEachWhere :: (*, * -> BOOLEAN), STRING, STRING, STRING, STRING, BOOLEAN -> this
  // Applies a predicate to each element of the array bound to ID and the other array bound "to" the mdoel,
  // if that predicate is true, the value of propFrom the element of the given array
  // is bound to the property of the element of the array bound to model:
  // NOTE: If propFrom is undefined, then elemen is bount to "propTo"
  // NOTE: If propTo is undefined, value from propFrom is written to index
  // NOTE: Both proprty of model and given array must be array!
  mergeFromEachWhere (pred, from, to, propFrom, propTo, clone) {
    return this.mergeToEachWhere(pred, from, this.model[to], propFrom, propTo, clone);
  }

  // mergeFromEach :: STRING, STRING, STRING, STRING, BOOLEAN -> this
  // Bind propFrom value to propTo property for every elem of array stored in ID from another property in model:
  // NOTE: If propFrom is undefined, then elemen is bount to "propTo"
  // NOTE: If propTo is undefined, value from propFrom is written to index
  // NOTE: Both proprty of model and given array must be array!
  mergeFromEach (from, to, propFrom, propTo, clone) {
    return mergeFromEachWhere(function (a,b) {
      return true;
    }, propFrom, propTo, clone);
  }

  // remove :: STRING -> this
  // Removes property from model:
  remove (id) {
    delete this.model[id];
    return this;
  }

  // chain :: MODELER, BOOLEAN -> this
  // Gets model from modeler instance and merges it to this instance's model
  chain (modeler, overwrite, clone) {
    return this.merge(modeler.get(), overwrite, clone);
  }

  // removeMany :: [STRING] -> this
  // Removes "many" properties from model:
  removeMany (ids) {
    ids.reduce(function (model, id) {
      delete model[id];
      return model;
    }, this);
    return this;
  }

  // getByFrom :: STRING, STRING, STRING, * -> this
  // Returns all the element "from" a propert of model and filters them by the "fromProp"
  // property matching the given value, storing the results "to" anothr property of model:
  getAllByFrom (from, to, fromProp, val) {
    return this.filterfrom(from, to, function (elem) {
      return elem[fromProp] === val;
    });
  }

  // getByFrom :: STRING, STRING, STRING, * -> this
  // Returns the first element where the given propVal matches the given fromProp of
  // If no matches are fouund, stores UNDEFINED "to" property in model:
  getByFrom (from, to, fromProp, val) {
    return this.getAllByFrom(from,to, fromProp, val).apto(to, function (to) {
      return to[0];
    });
  }

  // get :: VOID|(model -> model) -> model
  // "get" model from instance with optional callback:
  // NOTE: This is really syntactic sugar since all javascript object properties are public and mutable:
  get (fn) {
    return fn === undefined ? this.model : fn(model);
  }

  // log :: VOID -> model
  // Consoles out contents of model:
  log () {
    return this.ap(function (model) {
      console.log(model);
      return model;
    });
  }

  /**
   *
   *  Static Methods
   *
   */

  // Static factory method :: (OBJECT) -> bcmsModeler
  static init (model) {
    return new BCMSModeler(model);
  }

  // clone :: (OBJECT) -> OBJECT
  // Create clone of object by converting value to JSON and back to object:
  static clone (obj) {
    var _clone = JSON.stringify(obj);
    return JSON.parse(_clone);
  }

  // assoc :: OBJECT, STRING, * -> OBJECT
  // Binds value to property for given object, returning modfied object:
  static assoc (obj, prop, val) {
    obj[prop] = val;
    return obj;
  }

}
